import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY, of } from 'rxjs';
import {
  catchError,
  filter,
  map,
  mergeMap,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';

import { CustomersService } from './customers.service';
import { NewRelationsActions } from './new-relations.actions';

@Injectable()
export class NewRelationsEffects {
  connectPeople$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NewRelationsActions.subscribePeople),
      mergeMap(action =>
        this.customersService
          .subscribe({
            mysid: action.mysid,
            s: 'model',
            view: action.view ?? 'prs',
            prms: action.params,
            ...(action.q ? { q: action.q } : {}),
            ...(action.op ? { op: action.op } : {}),
          })
          .pipe(
            takeUntil(
              this.actions$.pipe(
                ofType(NewRelationsActions.unsubscribePeople),
                filter(unsubAction => action?.mysid === unsubAction?.mysid)
              )
            ),
            map((data: any) => {
              switch (data.op) {
                case 'query':
                case 'fast_query':
                  return data.res
                    ? NewRelationsActions.getPeopleSuccess({
                        customers: data.res,
                        meta: data.sts?.meta || {},
                        mysid: data.mysid,
                      })
                    : NewRelationsActions.getPeopleFailure(data.sts);

                case 'ins':
                  return data.res
                    ? NewRelationsActions.createPeopleSuccess({
                        customer: data.res,
                        mysid: data.mysid,
                      })
                    : NewRelationsActions.createPeopleError({
                        error: (data as any).sts,
                      });

                case 'del':
                  return data.res
                    ? NewRelationsActions.deletePeopleSuccess({
                        customer: data.res,
                        mysid: data.mysid,
                      })
                    : NewRelationsActions.deletePeopleError({
                        error: (data as any).sts,
                      });

                case 'upd':
                  return data.res
                    ? NewRelationsActions.updatePeopleSuccess({
                        customer: data.res,
                        mysid: data.mysid,
                      })
                    : NewRelationsActions.updatePeopleError({
                        error: (data as any).sts,
                      });

                default:
                  return { type: 'NONE' };
              }
            })
          )
      )
    )
  );

  createPeople$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NewRelationsActions.createPeople),
        switchMap(action =>
          this.customersService.createCustomer(action.customer).pipe(
            catchError(({ sts }) => {
              this.store.dispatch(
                NewRelationsActions.createPeopleError({ error: sts })
              );
              return EMPTY;
            })
          )
        )
      ),
    { dispatch: false }
  );

  deletePeople$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NewRelationsActions.deletePeople),
      switchMap(action =>
        this.customersService.deleteCustomer(action.relId).pipe(
          map(({ res }) =>
            NewRelationsActions.deletePeopleSuccess({ customer: res })
          ),
          catchError(({ sts }) =>
            of(NewRelationsActions.deletePeopleError({ error: sts }))
          )
        )
      )
    )
  );

  updatePeople$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NewRelationsActions.updatePeople),
      switchMap(action =>
        this.customersService
          .updateCustomer(action.customer, action.customer.relId)
          .pipe(
            map(({ res }) =>
              NewRelationsActions.updatePeopleSuccess({ customer: res })
            ),
            catchError(({ sts }) =>
              of(NewRelationsActions.updatePeopleError({ error: sts }))
            )
          )
      )
    )
  );

  sendInvitation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NewRelationsActions.sendInvitations),
      switchMap(action =>
        this.customersService.sendInvitation(action.relId, action.query).pipe(
          map(({ res }) =>
            NewRelationsActions.sendInvitationsSuccess({ sts: res.sts })
          ),
          catchError(({ sts }) =>
            of(NewRelationsActions.sendInvitationsError({ error: sts }))
          )
        )
      )
    )
  );

  connectInstitutions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NewRelationsActions.subscribeInstitutions),
      mergeMap(action =>
        this.customersService
          .subscribe({ mysid: action.mysid, s: 'model', view: 'acn' })
          .pipe(
            takeUntil(
              this.actions$.pipe(
                ofType(NewRelationsActions.unsubscribeInstitutions),
                filter(unsubAction => action?.mysid === unsubAction?.mysid)
              )
            )
          )
      ),
      map((data: any) => {
        switch (data.op) {
          case 'query':
            return data.res
              ? NewRelationsActions.getInstitutionsSuccess({
                  customers: data.res,
                  meta: data.sts?.meta || {},
                })
              : NewRelationsActions.getInstitutionsFailure(data.sts);

          case 'ins':
            return data.res
              ? NewRelationsActions.createInstitutionsSuccess({
                  customer: data.res,
                })
              : NewRelationsActions.createInstitutionsError({
                  error: (data as any).sts,
                });

          case 'del':
            return data.res
              ? NewRelationsActions.deleteInstitutionSuccess({
                  customer: data.res,
                })
              : NewRelationsActions.deleteInstitutionError({
                  error: (data as any).sts,
                });

          case 'upd':
            return data.res
              ? NewRelationsActions.updateInstitutionsSuccess({
                  customer: data.res,
                })
              : NewRelationsActions.updateInstitutionsError({
                  error: (data as any).sts,
                });

          default:
            return { type: 'NONE' };
        }
      })
    )
  );

  updateInstitutions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NewRelationsActions.updateInstitutions),
      switchMap(action =>
        this.customersService.updateInstitution(action.customer).pipe(
          map(({ res }) =>
            NewRelationsActions.updateInstitutionsSuccess({ customer: res })
          ),
          catchError(({ sts }) =>
            of(NewRelationsActions.updateInstitutionsError({ error: sts }))
          )
        )
      )
    )
  );

  updateInstitutionsLogo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NewRelationsActions.updateInstitutionLogo),
      mergeMap(action =>
        this.customersService.getTokenToUploadLogo(action.relId).pipe(
          map(({ res }) => {
            const formData = action.formData;
            formData.append('tkn', res.tkn);
            return formData;
          }),
          switchMap(formData => this.customersService.sendLogoImg(formData))
        )
      ),
      map(() => NewRelationsActions.updateInstitutionLogoSuccess()),
      catchError(error =>
        of(NewRelationsActions.updateInstitutionsError({ error }))
      )
    )
  );

  subscribeCustomerById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NewRelationsActions.subscribeCustomerById),
      mergeMap(({ mysid, usrId }) =>
        this.customersService
          .subscribeSingleContactById({
            mysid,
            s: 'model',
            view: 'prs',
            id: usrId,
            cmd: 'get',
          })
          .pipe(
            takeUntil(
              this.actions$.pipe(
                ofType(NewRelationsActions.unsubscribeCustomerById),
                filter(unsubAction => unsubAction.mysid === mysid)
              )
            ),
            map((data: any) => {
              if (data.op === 'get') {
                return data.res
                  ? NewRelationsActions.getCustomerByIdSuccess({
                      customer: data.res,
                    })
                  : NewRelationsActions.getCustomerByIdFailure({
                      error: data.sts,
                    });
              }
              return { type: 'NONE' };
            }),
            catchError(error =>
              of(NewRelationsActions.getCustomerByIdFailure({ error }))
            )
          )
      )
    )
  );

  getCustomerById$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NewRelationsActions.getCustomerById),
        tap(({ id, mysid }) => {
          this.customersService.getSingleContactById({
            id,
            mysid,
            view: 'prs',
          });
        })
      ),
    { dispatch: false }
  );

  constructor(
    private store: Store,
    private actions$: Actions,
    private customersService: CustomersService
  ) {}

  subscribeSingleInstitution$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NewRelationsActions.subscribeSingleInstitution),
      mergeMap(({ mysid, relAcnId }) =>
        this.customersService
          .subscribe({
            mysid,
            s: 'model',
            view: 'acn',
            cmd: 'query',
            q: [
              {
                k: 'relAcnId',
                op: 'EQ',
                v: [relAcnId],
              },
            ],
          })
          .pipe(
            takeUntil(
              this.actions$.pipe(
                ofType(NewRelationsActions.unsubscribeSingleInstitution),
                filter(unsubAction => unsubAction.mysid === mysid)
              )
            ),
            map((data: any) => {
              if (data.op === 'query') {
                return NewRelationsActions.getSingleInstitutionSuccess({
                  institution: data.res,
                });
              }
              return { type: 'NONE' };
            }),
            catchError(error =>
              of(NewRelationsActions.getSingleInstitutionFailure({ error }))
            )
          )
      )
    )
  );

  subscribeAvailableInstitutions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NewRelationsActions.subscribeAvailableInstitutions),
      mergeMap(({ mysid, itmId }) =>
        this.customersService
          .subscribe({
            mysid,
            s: 'model',
            view: 'acn',
            cmd: 'query',
            q: [
              {
                k: 'itmId',
                op: 'EQ',
                v: [itmId],
              },
              {
                k: 'view',
                op: 'EQ',
                v: ['available'],
              },
            ],
          })
          .pipe(
            takeUntil(
              this.actions$.pipe(
                ofType(NewRelationsActions.unsubscribeAvailableInstitutions),
                filter(unsubAction => unsubAction.mysid === mysid)
              )
            ),
            map((data: any) => {
              if (data.op === 'query') {
                return data.res
                  ? NewRelationsActions.getAvailableInstitutionsSuccess({
                      customers: data.res,
                      meta: data.sts?.meta || {},
                    })
                  : NewRelationsActions.getAvailableInstitutionsFailure({
                      error: data.sts,
                    });
              }
              return { type: 'NONE' };
            }),
            catchError(error =>
              of(NewRelationsActions.getAvailableInstitutionsFailure({ error }))
            )
          )
      )
    )
  );
}
